import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, EMPTY, map, Observable } from "rxjs";
import { toFieldStringArray } from "../../../../../common/utils/object-paths";
import { ApiService } from "../../../../api/packbo/api.service";
import { readItemsAdBlock } from "../../../../api/packbo/fn/items/read-items-ad-block";
import { readItemsCertifications } from "../../../../api/packbo/fn/items/read-items-certifications";
import { readItemsHeaderBlock } from "../../../../api/packbo/fn/items/read-items-header-block";
import { readItemsSuppliers } from "../../../../api/packbo/fn/items/read-items-suppliers";
import { ItemsSuppliers } from "../../../../api/packbo/models/items-suppliers";
import { setLoadingState } from "../../../../rxjs/set-loading-state.operator";
import { SearchResultsStore } from "./search-results.store";

export type SearchSuppliersParams = {
    searchTerm: string;
    postalCode: string;
    distance: string;
    page: number;
    certifications: number[];
    isEcoFriendly: boolean;
};

@Injectable()
export class SearchResultsService {
    private readonly store = inject(SearchResultsStore);
    private readonly apiService = inject(ApiService);
    private readonly http = inject(HttpClient);

    public fetchHeaderBlock(): Observable<void> {
        const headerBlockFields = toFieldStringArray({
            id: null,
            name: null,
            type: null,
            image: null,
            is_search_header: null,
            translations_refs: {
                languages_code: null,
                title: null,
                subtitle: null
            }
        });
        const headerBlockFilter = JSON.stringify({
            is_search_header: { _eq: true }
        });

        return this.apiService
            .invoke(readItemsHeaderBlock, { fields: headerBlockFields, filter: headerBlockFilter })
            .pipe(
                map((response) => this.store.setHeaderBlock(response.data?.[0] ?? null)),
                catchError(() => EMPTY)
            );
    }

    public fetchAds(): Observable<void> {
        const adsFields = toFieldStringArray({
            id: null,
            ads_refs: {
                ads_id: {
                    id: null,
                    image: null,
                    url: null
                }
            },
            ad_type: null,
            is_category_ad: null
        });
        const adsFilter = JSON.stringify({
            is_category_ad: { _eq: true },
            ad_type: { _eq: "targeted_horizontal_ad" }
        });

        return this.apiService.invoke(readItemsAdBlock, { fields: adsFields, filter: adsFilter }).pipe(
            map((ads) => this.store.setAdBlock(ads.data?.[0] ?? null)),
            catchError(() => EMPTY)
        );
    }

    public fetchCertifications(): Observable<void> {
        const certificationsFields = toFieldStringArray({
            id: null,
            logo: null,
            name: null
        });

        return this.apiService.invoke(readItemsCertifications, { fields: certificationsFields }).pipe(
            map((certifications) => this.store.setItemsCertifications(certifications.data ?? [])),
            catchError(() => EMPTY)
        );
    }

    public searchSuppliers(options: SearchSuppliersParams): Observable<void> {
        const { postalCode, distance, page } = options;
        if (page === 1) {
            this.store.resetPagination();
        }

        if (postalCode?.trim() && distance?.trim()) {
            return this.performLocationBasedSearch(options);
        } else {
            return this.performTextSearch(options);
        }
    }

    private performLocationBasedSearch(options: SearchSuppliersParams): Observable<void> {
        const { searchTerm, postalCode, distance, page, certifications, isEcoFriendly } = options;
        const customEndpointUrl = "/supplier-search/by-location";
        const params = new URLSearchParams({
            searchTerm: searchTerm || "",
            postalCode,
            distance,
            page: page.toString(),
            limit: this.store.pagination().itemsPerPage.toString(),
            isEcoFriendly: isEcoFriendly.toString()
        });

        certifications.forEach((certId) => {
            params.append("certifications", certId.toString());
        });

        const fullUrl = `${this.apiService.rootUrl}${customEndpointUrl}?${params}`;

        type CustomEndpointResponse = {
            data?: ItemsSuppliers[];
            meta?: { current_page?: number; total_pages?: number };
        };

        return this.http.get<CustomEndpointResponse>(fullUrl).pipe(
            setLoadingState(this.store, "loading", { minimumDuration: 500 }),
            map((response) => {
                const suppliers = response.data ?? [];
                const meta = response.meta ?? {};

                if (suppliers.length === 0) {
                    this.store.setSupplierImagesLoading(false);
                }

                this.store.setSuppliers(suppliers);
                this.store.setPagination({
                    currentPage: meta.current_page || page,
                    totalPages: meta.total_pages || 1
                });
            }),
            catchError(() => {
                this.store.setSupplierImagesLoading(false);
                this.store.setSuppliers([]);
                return EMPTY;
            })
        );
    }

    private performTextSearch(
        options: Pick<SearchSuppliersParams, "searchTerm" | "page" | "certifications" | "isEcoFriendly">
    ): Observable<void> {
        const { searchTerm, page, certifications, isEcoFriendly } = options;
        const supplierFields = this.getSupplierFields();
        const suppliersFilter = this.getSupplierFilter(searchTerm, certifications, isEcoFriendly);

        const limit = this.store.pagination().itemsPerPage;
        const offset = (page - 1) * limit;
        const sort = ["-package_ref.is_showcase_membership", "package_ref.hide_supplier_package", "name"];

        return this.apiService
            .invoke(readItemsSuppliers, {
                fields: supplierFields,
                filter: suppliersFilter,
                limit,
                offset,
                sort,
                meta: "filter_count"
            })
            .pipe(
                setLoadingState(this.store, "loading", { minimumDuration: 500 }),
                map((response) => {
                    const suppliers = response.data ?? [];
                    const totalItems = response.meta?.filter_count ?? 0;
                    const totalPages = Math.ceil(totalItems / limit);

                    this.store.setSuppliers(suppliers);
                    this.store.setPagination({
                        currentPage: page,
                        totalPages
                    });
                }),
                catchError(() => {
                    this.store.setSupplierImagesLoading(false);
                    this.store.setSuppliers([]);
                    return EMPTY;
                })
            );
    }

    private getSupplierFields(): string[] {
        return toFieldStringArray({
            id: null,
            name: null,
            banner: null,
            certifications_refs: {
                certifications_id: {
                    id: null,
                    logo: null,
                    name: null,
                    website: null,
                    translations_refs: {
                        languages_code: null,
                        name: null
                    }
                }
            },
            contact_ref: {
                id: null,
                email: null,
                phone_number: null,
                first_name: null,
                last_name: null,
                coord_ref: {
                    id: null,
                    address1: null,
                    address2: null,
                    locality: null,
                    postal_code: null
                }
            },
            coord_ref: {
                id: null,
                address1: null,
                address2: null,
                locality: null,
                postal_code: null
            },
            product_categories_refs: {
                product_categories_id: {
                    id: null,
                    image: null,
                    translations_refs: {
                        languages_code: null,
                        title: null
                    },
                    parent_ref: {
                        id: null,
                        image: null
                    }
                }
            },
            email: null,
            is_active: null,
            logo: null,
            package_ref: {
                id: null,
                package_color: null,
                name: null,
                hide_supplier_package: null,
                is_showcase_membership: null,
                package_text_color: null,
                translations_refs: {
                    languages_code: null,
                    title: null
                }
            },
            phone_number: null,
            status: null,
            website_url: null,
            translations_refs: {
                languages_code: null,
                summary: null,
                description: null,
                slug: null
            },
            is_eco_friendly: null,
            files_refs: {
                directus_files_id: {
                    id: null,
                    type: null
                },
                id: null
            }
        });
    }

    private getSupplierFilter(searchTerm: string, certifications: number[], isEcoFriendly: boolean): string {
        const filter = {
            is_active: { _eq: true }
        } as Record<string, unknown>;

        if (searchTerm && searchTerm.trim() !== "") {
            filter["_or"] = [
                {
                    name: { _contains: searchTerm }
                },
                {
                    keywords: { _contains: searchTerm }
                },
                {
                    product_categories_refs: {
                        product_categories_id: {
                            translations_refs: {
                                title: { _contains: searchTerm }
                            }
                        }
                    }
                }
            ];
        }

        if (certifications.length) {
            filter["certifications_refs"] = {
                certifications_id: {
                    id: { _in: certifications }
                }
            };
        }

        if (isEcoFriendly) {
            filter["is_eco_friendly"] = { _eq: true };
        }

        return JSON.stringify(filter);
    }
}
